import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { Sidebar } from "syngenta-digital-cropwise-react-ui-kit";
import "./sidebar.less";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../../lib/appContext/AppContext";
import { appURLs } from "../../lib/appRoutes/appUrls";
import { useTrialsRouter } from "../custom-hooks/useTrialsRouter";
import { useProtocolsRouter } from "../custom-hooks/useProtocolsRouter";
import { useTrackingEvent } from "../custom-hooks/useTracking";
import {
  ProtocolAmplitudeEvent,
  TrialAmplitudeEvent,
} from "../utils/amplitudeEvents";

interface RouteInterface {
  name: string;
  path: string;
}

export const SidebarMenu: React.FC = (props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { stateValue, setStateValue } = useAppState();
  const {
    orgId: trialsOrgId,
    workspaceId: trialsWorkspaceId,
    farmId: trialsFarmId,
  } = useTrialsRouter();
  const {
    orgId: protocolsOrgId,
    workspaceId: protocolsWorkspaceId,
    farmId: protocolsFarmId,
  } = useProtocolsRouter();
  const orgId = trialsOrgId ?? protocolsOrgId;
  const workspaceId = trialsWorkspaceId ?? protocolsWorkspaceId;
  const farmId = trialsFarmId ?? protocolsFarmId;

  const isAssessment = window.location.pathname.toString().includes("reports")
    ? "REPORTS"
    : "";

  const [active, setActive] = useState(isAssessment);
  const routes: Record<string, RouteInterface> = {
    workspace: {
      name: "My Workspace",
      path: `${appURLs.MY_CROPWISE}`,
    },
  };

  if (orgId && workspaceId && farmId) {
    routes.protocols = {
      name: "Protocols",
      path: `${appURLs.PROTOCOLS}/workspace/${workspaceId}/org/${orgId}/farm/${farmId}`,
    };
    routes.trials = {
      name: "Trials",
      path: `${appURLs.TRIALS}/workspace/${workspaceId}/org/${orgId}/farm/${farmId}`,
    };
  }
  const routesMap = Object.keys(routes);

  useEffect(() => {
    const routeKey = routesMap?.find((route) => {
      const routePath = `${routes[route].path}`;
      return pathname === routePath || pathname.startsWith(`${routePath}/`);
    });
    setActive(routeKey ?? "");
  }, [pathname]);

  const trackTrialsEvents = useTrackingEvent({
    page: TrialAmplitudeEvent.TRIALS_PAGE,
    group: TrialAmplitudeEvent.TRIALS_GROUP,
    module: TrialAmplitudeEvent.TRIALS_GROUP,
  });
  const trackProtocolsEvents = useTrackingEvent({
    page: ProtocolAmplitudeEvent.PROTOCOLS_PAGE,
    group: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
    module: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
  });

  return (
    <Sidebar
      data-cy="sidebar"
      test-id="sidebar"
      theme="dark"
      selectedKeys={[active]}
      collapsedMenu={true}
      // @ts-expect-error suppressing below error
      menuItems={routesMap.map((route) => ({
        key: route,
        label: routes[route].name,
        icon: (
          <Tooltip title={routes[route].name} placement="right">
            <img
              className="sidebar-icon active"
              alt={route.toLowerCase()}
              src={require(`../../assets/images/${route
                .toLowerCase()
                .replaceAll("_", "-")}.svg`)}
            />
          </Tooltip>
        ),
        onClick: async (e: any) => {
          if (active.includes("trials")) {
            void trackTrialsEvents({
              event: TrialAmplitudeEvent.TRIAL_MENU_CLICK,
              originAction: e.key,
            });
          } else if (active.includes("protocols")) {
            void trackProtocolsEvents({
              event: ProtocolAmplitudeEvent.PROTOCOL_MENU_CLICK,
              originAction: e.key,
            });
          }
          if (route === "organizations") {
            setStateValue({
              ...stateValue,
              orgsData: null,
              orgValue: null,
              farmValue: null,
              assigneeValue: null,
            });
          } else if (route === "farms") {
            setStateValue({
              ...stateValue,
              farmValue: null,
              assigneeValue: null,
            });
          } else if (route === "workspace") {
            setActive(route);
            navigate(`${routes[route].path}`);
            setStateValue({
              ...stateValue,
              farmValue: null,
              assigneeValue: null,
            });
          } else {
            setActive(route);
            navigate(`${routes[route].path}/`);
          }
        },
      }))}
    />
  );
};
